import { Box, CircularProgress, Typography } from '@mui/material';
import type { SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { ApiErrorI } from 'declarations/interfaces/errors';

import { InsightsDealerSelect } from './components/InsightsDealerSelect/InsightsDealerSelect';
import { ReportItem } from './components/ReportItem';
import SelectCategoryReport from './components/SelectCategories/SelectCategoryReport';
import type {
  DashboardI,
  GetDashboardsResponseDTO,
  GetEmbedUrlResponseDTO,
} from './declarations/reporting';
import { useFetchDashboardIds, useFetchEmbedUrl } from './hooks/useReporting';

const InsightsContent = (): JSX.Element => {
  const [dashboards, setDashboards] = useState<DashboardI[]>();
  const [selectedDashboard, setSelectedDashboard] = useState<DashboardI | undefined>(undefined);
  const [embedUrl, setEmbedUrl] = useState<GetEmbedUrlResponseDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const { roofTopsList, getSearchParam, updateSearchParamsArray } = useTickets();

  const selectedDealerId = getSearchParam(TicketsRefinementKeysE.RoofTopId) ?? null;

  const roofTopIds = useMemo((): string[] => {
    const list = roofTopsList ?? [];
    if (selectedDealerId && selectedDealerId.trim()) {
      return [selectedDealerId.trim().toUpperCase()];
    }
    return list.map((r): string => r.id.toUpperCase());
  }, [roofTopsList, selectedDealerId]);

  const handleDealerChange = useCallback(
    (id: string | null): void => {
      updateSearchParamsArray(TicketsRefinementKeysE.RoofTopId, id ? [id] : null);
    },
    [updateSearchParamsArray]
  );

  useFetchDashboardIds({
    onSuccess: (data: GetDashboardsResponseDTO): void => {
      const newDashboards = (data as DashboardI[]).map(
        (dashboard): DashboardI => ({
          ...dashboard,
        })
      );
      const defaultDashboard = newDashboards[0];
      setDashboards(newDashboards);
      setSelectedDashboard(defaultDashboard);
      setLoading(false);
    },
    onError: (error: { code: SetStateAction<string | null> }): void => {
      setLoading(false);
      setError(error.code);
    },
  });

  useFetchEmbedUrl({
    dashboardId: selectedDashboard?.dashboardId ?? '',
    roofTopIds,
    onSuccess: (data: GetEmbedUrlResponseDTO): void => {
      setEmbedUrl(data);
      setLoading(false);
    },
    onError: (error: ApiErrorI): void => {
      setLoading(false);
      if (error.data?.status === 403) {
        setError('You do not have access to this dashboard.');
      } else {
        setError(error.code);
      }
    },
  });

  const handleDashboardClick = (dashboard: DashboardI): void => {
    setError(null);
    setSelectedDashboard(dashboard);
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'inherit',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem',
          justifyContent: 'flex-start',
          alignItems: 'center',
          margin: '8px 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <InsightsDealerSelect
            roofTops={roofTopsList ?? []}
            roofTopsList={roofTopsList}
            value={selectedDealerId}
            onChange={handleDealerChange}
          />
          {selectedDashboard && (
            <SelectCategoryReport
              dashboards={dashboards}
              value={selectedDashboard}
              onChange={handleDashboardClick}
              isLoading={loading}
            />
          )}
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
            <Typography variant="h5" color="error">
              {error}
            </Typography>
          </Box>
        )}

        {!loading && embedUrl && (
          <Box sx={{ flexGrow: 1, height: '100%', display: 'flex' }}>
            <ReportItem
              id={embedUrl.dashboardId}
              route={embedUrl.embedURL ?? ''}
              roofTopIds={roofTopIds}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InsightsContent;
